import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { useOverlay } from "@/hooks/use-overlay";
import { useEffect, useId } from "react";
import { setRuntimeDirty } from "@/runtime/dirty";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  className,
}: DialogProps) {
  const runtimeDirtyId = useId();
  useEffect(() => {
    setRuntimeDirty(runtimeDirtyId, open);
    return () => setRuntimeDirty(runtimeDirtyId, false);
  }, [runtimeDirtyId, open]);
  useOverlay(open, onClose);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-overlay backdrop-blur-sm animate-overlay-in"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          "relative z-50 w-full max-w-md max-h-[85vh] overflow-y-auto rounded-lg bg-card p-6 shadow-modal animate-dialog-in scrollbar-thin",
          className
        )}
      >
        {title && (
          <div className="mb-4">
            <h2 className="text-lg font-semibold tracking-tight text-foreground">
              {title}
            </h2>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">
                {description}
              </p>
            )}
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}
